import { zodResolver } from "@hookform/resolvers/zod";
import { useFieldArray, useForm } from "react-hook-form";
import * as z from "zod";
import { onboardingLicenseValues } from "../utils/onboarding-license-values";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { format } from "date-fns";
import {
  ArrowRight,
  MapPin,
  ShieldCheck,
  AlertCircle,
  Plus,
  Info,
} from "lucide-react";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { useState } from "react";
import { useTRPC } from "@/trpc";
import Loading from "@/components/Loading";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { OnboardingStepHeader } from "./onboarding-step-header";
import { ONBOARDING_FORM_STYLES as styles } from "./onboarding-form-styles";
import { OnboardingStateLicenseCard } from "./onboarding-license-fields";

const licenseSchema = z.object({
  credentialId: z.string().optional(),
  legacyStateCode: z.string().optional(),
  state: z.string().length(2, "State code must be 2 characters"),
  license_number: z
    .string()
    .max(255, "License number must be less than 255 characters"),
  expiration_date: z.date().nullable(),
});

const formSchema = z.object({
  stateLicenses: z.array(licenseSchema),
  imlcLicense: z.object({
    has_license: z.boolean({
      required_error: "Please select whether you have an IMLC license",
      invalid_type_error: "Please select whether you have an IMLC license",
    }),
  }),
  confirmNoStateLicenses: z.boolean().optional(),
});

export type LicenseOnboardingFormValues = z.infer<typeof formSchema>;

enum ImlcAnswer {
  Yes = "yes",
  No = "no",
}

function getImlcAnswer(value?: boolean): string {
  if (value === true) {
    return ImlcAnswer.Yes;
  }
  if (value === false) {
    return ImlcAnswer.No;
  }
  return "";
}

interface LicensesOnboardingFormProps {
  onComplete: () => void;
  userId: string;
  hasCompleted?: boolean;
}

export function LicensesOnboardingForm({
  onComplete,
  userId,
  hasCompleted = false,
}: LicensesOnboardingFormProps) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [saveStatus, setSaveStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [expandedLicenses, setExpandedLicenses] = useState<Set<string>>(
    new Set(),
  );

  const credentialsQuery = useSuspenseQuery(
    trpc.providerOnboarding.getSavedDetails.queryOptions(),
  );
  const saved = onboardingLicenseValues(credentialsQuery.data.licenses);
  const [removedCredentialIds, setRemovedCredentialIds] = useState<Set<string>>(
    new Set(),
  );
  const [removedLegacyStateCodes, setRemovedLegacyStateCodes] = useState<
    Set<string>
  >(new Set());

  const mutation = useMutation(
    trpc.providerOnboarding.saveLicenses.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.providerOnboarding.getSavedDetails.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.credentials.getMyCredentials.queryKey({
            switchUserId: userId,
          }),
        });
        setSaveStatus("idle");
        setErrorMessage(null);

        await queryClient.invalidateQueries({
          queryKey: trpc.provider.getProviderUser.queryKey(),
        });
        onComplete();
      },
      onError: (error) => {
        setSaveStatus("error");
        setErrorMessage(
          error instanceof Error ? error.message : "Failed to save changes",
        );
        setTimeout(() => setSaveStatus("idle"), 4000);
      },
    }),
  );

  const form = useForm<LicenseOnboardingFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      stateLicenses: saved.stateLicenses,
      imlcLicense: {
        has_license: saved.hasImlc ?? (hasCompleted ? false : undefined),
      },
      confirmNoStateLicenses: false,
    },
  });

  const {
    fields: licenseFields,
    append: appendLicense,
    remove: removeLicenseRow,
  } = useFieldArray({
    control: form.control,
    name: "stateLicenses",
  });

  // Helper to check if a license has data that should auto-expand it
  const shouldAutoExpand = (index: number): boolean => {
    const credentialId = form.getValues(`stateLicenses.${index}.credentialId`);
    const licenseNumber = form.getValues(
      `stateLicenses.${index}.license_number`,
    );
    const expirationDate = form.getValues(
      `stateLicenses.${index}.expiration_date`,
    );
    return (
      Boolean(credentialId) ||
      (licenseNumber && licenseNumber.trim() !== "") ||
      expirationDate !== null
    );
  };

  // Check if a license is expanded (either manually toggled or auto-expanded based on data)
  const isLicenseExpanded = (fieldId: string, index: number): boolean => {
    // If manually toggled, use that state
    if (expandedLicenses.has(fieldId)) {
      return true;
    }
    if (expandedLicenses.has(`collapsed-${fieldId}`)) {
      return false;
    }
    // Otherwise, auto-expand if has data
    return shouldAutoExpand(index);
  };

  const toggleLicenseExpanded = (fieldId: string, index: number) => {
    setExpandedLicenses((prev) => {
      const next = new Set(prev);
      const isCurrentlyExpanded = isLicenseExpanded(fieldId, index);

      // Clear any existing state for this field
      next.delete(fieldId);
      next.delete(`collapsed-${fieldId}`);

      // Set explicit state
      if (isCurrentlyExpanded) {
        next.add(`collapsed-${fieldId}`);
      } else {
        next.add(fieldId);
      }
      return next;
    });
  };

  function onSubmit(data: LicenseOnboardingFormValues) {
    // Custom validation: if no state licenses, must confirm they don't have any
    if (data.stateLicenses.length === 0 && !data.confirmNoStateLicenses) {
      form.setError("confirmNoStateLicenses", {
        message:
          "Please confirm you don't have any state medical licenses, or add your licenses above",
      });
      return;
    }

    setSaveStatus("saving");
    setErrorMessage(null);

    mutation.mutate({
      licenses: data.stateLicenses.map((license) => ({
        credentialId: license.credentialId,
        ...(license.legacyStateCode
          ? { legacyStateCode: license.legacyStateCode }
          : {}),
        stateCode: license.state,
        number: license.license_number.trim() || null,
        expiresAt: license.expiration_date
          ? format(license.expiration_date, "yyyy-MM-dd")
          : null,
      })),
      removedCredentialIds: [...removedCredentialIds],
      removedLegacyStateCodes: [...removedLegacyStateCodes],
      hasImlc: data.imlcLicense.has_license,
    });
  }

  function removeLicense(index: number) {
    const credentialId = form.getValues(`stateLicenses.${index}.credentialId`);
    if (credentialId) {
      setRemovedCredentialIds(
        (previous) => new Set([...previous, credentialId]),
      );
    }
    const legacyStateCode = form.getValues(
      `stateLicenses.${index}.legacyStateCode`,
    );
    if (legacyStateCode) {
      setRemovedLegacyStateCodes(
        (previous) => new Set([...previous, legacyStateCode]),
      );
    }
    removeLicenseRow(index);
  }

  function hasSavedLicense(index: number) {
    const license = form.getValues(`stateLicenses.${index}`);
    return Boolean(license.credentialId || license.legacyStateCode);
  }

  const handleAddLicense = () => {
    appendLicense({
      state: "",
      license_number: "",
      expiration_date: null,
    });
    // Reset confirmation checkbox when adding a license
    form.setValue("confirmNoStateLicenses", false);
  };

  return (
    <div>
      <OnboardingStepHeader
        title="Review Your Medical Licenses"
        description={
          licenseFields.length > 0
            ? "Review the state medical licenses already on your profile and update any information as needed."
            : "Add your state medical licenses and confirm your IMLC status."
        }
      />

      <Form {...form}>
        {errorMessage && (
          <div
            role="alert"
            className={`${styles.error} mb-6 flex items-start gap-3`}
          >
            <AlertCircle
              className="mt-0.5 size-5 shrink-0"
              aria-hidden="true"
            />
            <p>{errorMessage}</p>
          </div>
        )}
        {form.formState.errors.confirmNoStateLicenses && (
          <div
            role="alert"
            className={`${styles.error} mb-6 flex items-start gap-3`}
          >
            <AlertCircle
              className="mt-0.5 size-5 shrink-0"
              aria-hidden="true"
            />
            <p>{form.formState.errors.confirmNoStateLicenses.message}</p>
          </div>
        )}

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <section
            className={styles.section}
            aria-labelledby="onboarding-imlc-heading"
          >
            <div className="mb-5 flex items-start gap-3">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/5 text-primary">
                <ShieldCheck className="size-5" aria-hidden="true" />
              </span>
              <div>
                <h3
                  id="onboarding-imlc-heading"
                  className="font-semibold text-slate-900"
                >
                  IMLC License
                </h3>
                <p className="mt-1 text-sm leading-6 text-slate-500">
                  Confirm your IMLC status before continuing.
                </p>
              </div>
            </div>
            <FormField
              control={form.control}
              name="imlcLicense.has_license"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-x-3">
                    <FormLabel className={styles.label}>
                      Have an IMLC License?{" "}
                      <span className="text-destructive" aria-hidden="true">
                        *
                      </span>
                    </FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="link"
                          size="none"
                          className="min-h-11 gap-1.5 p-0 text-sm underline-offset-4"
                          type="button"
                        >
                          <Info className="size-4" aria-hidden="true" />
                          Learn more
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 max-w-[calc(100vw-2rem)] rounded-xl text-sm">
                        <div className="space-y-2">
                          <h4 className="font-medium">
                            What is an IMLC License?
                          </h4>
                          <p>
                            The Interstate Medical Licensure Compact (IMLC) is
                            an agreement between U.S. states that allows
                            physicians to practice medicine across state lines
                            more easily.
                          </p>
                          <p>
                            With an IMLC license, you can obtain expedited
                            licensure in other participating states without
                            having to complete the full application process in
                            each state.
                          </p>
                          <p className="text-muted-foreground">
                            This helps healthcare providers practice
                            telemedicine and work in multiple states more
                            efficiently.
                          </p>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <Select
                    value={getImlcAnswer(field.value)}
                    onValueChange={(value) =>
                      field.onChange(value === ImlcAnswer.Yes)
                    }
                  >
                    <FormControl>
                      <SelectTrigger
                        ref={field.ref}
                        className={`${styles.input} focus:border-primary focus:ring-primary/15 focus:ring-offset-0`}
                        onBlur={field.onBlur}
                      >
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="rounded-xl">
                      <SelectItem
                        value={ImlcAnswer.Yes}
                        className="min-h-11 rounded-lg"
                      >
                        Yes
                      </SelectItem>
                      <SelectItem
                        value={ImlcAnswer.No}
                        className="min-h-11 rounded-lg"
                      >
                        No
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </section>

          <section
            className={styles.section}
            aria-labelledby="onboarding-state-licenses-heading"
          >
            <div className="mb-5 flex items-start gap-3">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/5 text-primary">
                <MapPin className="size-5" aria-hidden="true" />
              </span>
              <div>
                <h3
                  id="onboarding-state-licenses-heading"
                  className="font-semibold text-slate-900"
                >
                  State Medical Licenses
                </h3>
                <p className="mt-1 text-sm leading-6 text-slate-500">
                  Add each state where you hold a medical license. License
                  numbers and expiration dates are optional.
                </p>
              </div>
            </div>

            {licenseFields.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/70 p-5 text-center sm:p-6">
                <p className="font-medium text-slate-700">
                  No state licenses found.
                </p>
                <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-slate-500">
                  Add a license, or confirm below if you don&apos;t have any
                  state medical licenses.
                </p>
                <Button
                  type="button"
                  onClick={handleAddLicense}
                  className={`${styles.primaryButton} my-5 h-auto min-h-12 whitespace-normal px-3 py-3`}
                >
                  <Plus className="size-4 shrink-0" aria-hidden="true" />
                  Add Your First License
                </Button>
                <FormField
                  control={form.control}
                  name="confirmNoStateLicenses"
                  render={({ field }) => (
                    <FormItem className="flex items-start gap-3 space-y-0 rounded-xl border border-slate-200 bg-white p-4 text-left">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          className="mt-1 shrink-0"
                        />
                      </FormControl>
                      <div className="space-y-2">
                        <FormLabel className="block cursor-pointer text-sm font-normal leading-6 text-slate-700">
                          I confirm that I don&apos;t have any state medical
                          licenses
                        </FormLabel>
                        <FormMessage />
                      </div>
                    </FormItem>
                  )}
                />
              </div>
            ) : (
              <div className="space-y-4">
                {licenseFields.map((field, index) => (
                  <OnboardingStateLicenseCard
                    key={field.id}
                    control={form.control}
                    index={index}
                    readOnlyState={hasSavedLicense(index)}
                    expanded={isLicenseExpanded(field.id, index)}
                    onRemove={() => removeLicense(index)}
                    onToggle={() => toggleLicenseExpanded(field.id, index)}
                  />
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  onClick={handleAddLicense}
                  className={`${styles.secondaryButton} h-auto min-h-12 w-full whitespace-normal px-3 py-3`}
                >
                  <Plus className="size-4 shrink-0" aria-hidden="true" />
                  Add Another State License
                </Button>
              </div>
            )}
          </section>

          <div className={styles.footer}>
            <p className="text-xs leading-5 text-slate-500 sm:mr-auto">
              Review your licenses before continuing.
            </p>
            <Button
              type="submit"
              size="lg"
              className={styles.primaryButton}
              disabled={saveStatus === "saving"}
              aria-busy={saveStatus === "saving"}
            >
              {saveStatus === "saving" ? (
                <>
                  <Loading className="size-4" />
                  Saving…
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="size-4" aria-hidden="true" />
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
